import React, { useRef, useEffect, useCallback } from 'react';

interface TrustedAppsVettingFAQProps {
  open: boolean;
  onClose: () => void;
}

function TrustedAppsVettingFAQ({ open, onClose }: TrustedAppsVettingFAQProps) {
  const faqRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (faqRef.current && open) {
        if (faqRef.current.contains(e.target as Node)) return;
        onClose();
      }
    },
    [open, onClose]
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside, false);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside, false);
      };
    }
  }, [open, handleClickOutside]);

  return (
    <div className="fixed top-0 right-0 h-full w-full z-[99998]" style={{ pointerEvents: open ? 'auto' : 'none', zIndex: 99998 }}>
      <div
        ref={faqRef}
        className="fixed top-0 right-0 h-full bg-white shadow-2xl border-l border-gray-200 z-[99999] p-8 overflow-y-auto"
        style={{
          width: '25vw',
          willChange: 'transform',
          transition: 'transform 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          zIndex: 10000
        }}
        data-test="faq-popout"
      >
        <h2 className="text-xl font-bold mb-4">Data Privacy Vetting FAQ</h2>
        <ul className="space-y-4 text-gray-700">
          <li>
            <strong>How are applications vetted for Data Privacy?</strong><br />
            <div className="mt-2 text-sm text-gray-500">
              All applications included in the TrustEd Apps Program undergo an initial Data Privacy vetting as part of the onboarding process. This review evaluates the application’s published privacy policies and disclosures against <a
                href="https://www.imsglobal.org/spec/privacy/v1p0"
                target="_blank"
                rel="noopener noreferrer"
                className="break-words"
                style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}
              >
              1EdTech’s Student Data Privacy Standard Specification
              </a> requirements.
            </div>
          </li>
          <li>
            <strong>How often are applications reviewed after the initial vetting?</strong><br />
            <div className="mt-2 text-sm text-gray-500">
              Apps and extensions are reviewed at member requests via the member request form.
            </div>
            <div className="mt-2 text-sm text-gray-500">
              Given the size and scale of the TrustEd Apps ecosystem—more than 13,000 applications and extensions—ongoing reviews are conducted based on member requests via the TrustEd App Management Suite's (TAMS) App Vetting Request Form. This ensures that vettings are prioritized where they provide the most value to districts, institutions, and educators. To request a revet of an App: (If the Institution finds a vetting with a date older than the privacy policy updates.) follow the original app vetting request form, but add the year the privacy policy was updated as part of the product title. Example vetting request via TAMS- XYZ Privacy Updated 2024T.
            </div>
          </li>
          <li>
            <strong>How often is an application that has earned the 1EdTech Data Privacy certification re-certified?</strong><br />
            <div className="mt-2 text-sm text-gray-500">
              Applications that earn their 1EdTech Data Privacy Certification are re-certified annually.
            </div>
            <div className="mt-2 text-sm text-gray-500">
              Annual recertification is based on:
            </div>
            <ul className="list-disc list-inside mt-2 text-gray-500">
              <li className="text-sm mt-2 text-gray-500">The outcome of the current privacy review</li>
              <li className="text-sm mt-2 text-gray-500">Updates or changes shared by the provider</li>
              <li className="text-sm mt-2 text-gray-500">Feedback and clarifications from the provider during the review process</li>
            </ul>
            <div className="mt-2 text-sm text-gray-500">
              This annual cycle helps ensure certified applications continue to meet 1EdTech’s Data Standard Specification requirements over time.
            </div>
          </li>
          <li>
            <strong>Why does 1EdTech emphasize human expertise in the reviews?</strong><br />
            <div className="mt-2 text-sm text-gray-500">
              Student data privacy requires careful interpretation, professional judgment, and accountability. While automation can support efficiency, 1EdTech staff conduct in-depth reviews to ensure that privacy practices align with the 1EdTech Data Privacy Standard Specification and that trust remains at the center of the TrustEd Apps Program.
            </div>
          </li>
          <li>
            <strong>How can members request an app vetting?</strong><br />
            <div className="mt-2 text-sm text-gray-500">
              Members can request a 1EdTech Data Privacy app vetting for specific applications directly through the TrustEd Apps Management Suite App Vetting Request Form. These requests help guide ongoing reviews and ensure that member priorities drive our work.<br />
            </div>
            <div className="mt-2 text-sm text-gray-500">
              <a
                href="https://vimeo.com/1109090145"
                target="_blank"
                rel="noopener noreferrer"
                className="break-words"
                style={{ wordBreak: 'normal', overflowWrap: 'break-word' }}
              >
                Here’s a video link with more information for your Provider partners on the program.
              </a>
            </div>
          </li>
        </ul>
        <button
          type="button"
          className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          onClick={onClose}
          aria-label="Close FAQ"
        >
          <span aria-hidden="true">&times;</span>
        </button>
      </div>
    </div>
  );
}

export default TrustedAppsVettingFAQ;
